import { LogLevel } from "@grafana/faro-web-sdk";
import { trace } from "@opentelemetry/api";
import { getActiveClickSpanContext, TraceEntry } from "./traceContextStore";

/**
 * Log levels for the logger service
 */
export enum LoggerLevel {
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
  DEBUG = 'debug'
}

/**
 * Logger service that abstracts away the underlying logging implementation
 * Components should use this service for all logging needs
 */
class LoggerService {
  private mapToFaroLevel(level: LoggerLevel): LogLevel {
    switch (level) {
      case LoggerLevel.ERROR:
        return LogLevel.ERROR;
      case LoggerLevel.WARN:
        return LogLevel.WARN;
      case LoggerLevel.DEBUG:
        return LogLevel.DEBUG;
      case LoggerLevel.INFO:
      default:
        return LogLevel.INFO;
    }
  }

  /**
   * Log a message with optional context and prefix
   * @param message - The log message
   * @param level - Log level (INFO, WARN, ERROR, DEBUG)
   * @param context - Optional context object to include in the log
   * @param prefix - Optional prefix to add to the message (e.g., "moveToRIP")
   */
  log(
    message: string,
    level: LoggerLevel = LoggerLevel.INFO,
    context?: any,
    prefix?: string
  ): void {
    try {
      // Internal implementation detail - components don't need to know about this
      const faro = (window as any)?.faroInstance;
      if (faro) {
        const prefixedMessage = prefix ? `[${prefix}] ${message}` : message;
        const logMessage = context 
          ? `${prefixedMessage} | Context: ${JSON.stringify(context)}`
          : prefixedMessage;
        const otelSpan = trace.getActiveSpan()?.spanContext();
        const tc: TraceEntry | null = otelSpan
          ? { traceId: otelSpan.traceId, spanId: otelSpan.spanId }
          : getActiveClickSpanContext();
        faro.api.pushLog([logMessage], {
          level: this.mapToFaroLevel(level),
          ...(tc ? {
            traceContext: tc,
            context: { trace_id: tc.traceId }
          } : {})
        });
      }
      
      // Also log to console for local debugging
      const consoleMessage = prefix ? `[${prefix}] ${message}` : message;
      if (level === LoggerLevel.ERROR) {
        console.error(consoleMessage, context || '');
      } else if (level === LoggerLevel.WARN) {
        console.warn(consoleMessage, context || '');
      } else {
        console.log(consoleMessage, context || '');
      }
    } catch (e) {
      console.error('Failed to log:', e);
    }
  }
}

// Export a singleton instance
export const logger = new LoggerService();
